// The canonical apply path and offering-interest tags. Every "Apply for coaching" CTA routes into the
// /apply funnel. /book is a separate, narrower page (coldapproach.io/book):
// it embeds the 15-minute application-call widget directly for a prospect
// who already applied or already has a lead relationship (the /apply
// booking step and the booking-confirmation email) - it is never
// a substitute for the "Apply for coaching" CTA, which always points here.

// ---------------------------------------------------------------------------
// Offering-interest tags. The application deliberately never asks "what are
// you applying for" (owner, 2026-07-01) — the interest signal is WHICH
// offering page the visitor clicked Apply from. Every offering page's Apply
// CTA carries `?offer=<tag>`, the tag is stamped as `program` on the stored
// lead/application, and the daily pulse rolls up interest by offering. A bare
// /apply visit stays untagged (never a silent default — that mislabeled every
// lead "bootcamp" until 2026-07-10).
// ---------------------------------------------------------------------------

export type ProgramTag =
  | "bootcamp"
  | "call"
  | "custom"
  | "mentorship"
  | "ai-photos"
  | "profile-makeover"
  | "texting-mastery"

export const PROGRAM_TAGS: readonly ProgramTag[] = [
  "bootcamp",
  "call",
  "custom",
  "mentorship",
  "ai-photos",
  "profile-makeover",
  "texting-mastery",
]

/** Human label for a stored `program` value (pulse, admin). Unknown/legacy
 * values render as-is. */
export function programLabel(tag: string | null | undefined): string | null {
  switch (tag) {
    case "bootcamp":
      return "Bootcamp"
    case "call":
      return "1-on-1 calls"
    case "custom":
      return "Custom coaching"
    case "mentorship":
      return "Mentorship"
    case "ai-photos":
      return "AI Photos"
    case "profile-makeover":
      return "Online Dating Concierge"
    case "texting-mastery":
      return "Texting Mastery"
    default:
      return tag || null
  }
}

export function programTagByKey(key: string | null | undefined): ProgramTag | undefined {
  return PROGRAM_TAGS.find((tag) => tag === key)
}

/** Offering page -> tag, for CTAs that live on shared chrome (the masthead):
 * when the visitor clicks the global Apply button WHILE on an offering page,
 * the lead still records which offering they were reading. */
export const PROGRAM_TAG_BY_PATH: Record<string, ProgramTag> = {
  "/bootcamp": "bootcamp",
  "/calls": "call",
  "/mentorship": "mentorship",
  "/ai-photos": "ai-photos",
  "/online-dating-concierge": "profile-makeover",
  // Texting Mastery is RETIRED as an offering (owner, 2026-07-17: "we no
  // longer have texting mastery") - no path mapping, so the pulse stops
  // seeding its zero row. The "texting-mastery" ProgramTag + label survive
  // only so historical lead rows still render.
}

/** The apply path carrying an offering-interest tag. */
export function applyHref(tag?: ProgramTag): string {
  return tag ? `/apply?offer=${tag}` : "/apply"
}
